import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  createSetReceiptAttachmentIds,
  getOrgMembers,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.actions'
import {
  selectErrorDetailMapFromState,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectPostingVirtualCardFromState,
  selectReceiptAttachmentModelsList,
  selectTopLevelErrorFromState,
  selectUploadingAttachmentsFromState,
  selectUsersFullPaginatedData,
} from 'src/app/RequestVirtualCard/redux/requestVirtualCard.selectors'
import RequestVirtualCardPickContent from 'src/components/RequestVirtualCardPickContent/RequestVirtualCardPickContent'
import RequestVirtualCardRecurringContent from 'src/components/RequestVirtualCardRecurringContent/RequestVirtualCardRecurringContent'
import RequestVirtualCardStandardContent from 'src/components/RequestVirtualCardStandardContent/RequestVirtualCardStandardContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { SupportedBrands } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useFlyoutDispatch } from 'src/hooks'
import { uploadAttachments } from 'src/redux/actions/data/receiptAttachments.actions'
import { postVirtualCardAndOpenToastAndCloseFlyout } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCreditCardsActiveSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import {
  selectAppName,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'

export enum RequestVirtualCardScreen {
  Pick,
  Standard,
  Recurring,
}

const RequestVirtualCard: React.FC = () => {
  const dispatch = useFlyoutDispatch()

  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const postingVirtualCard = useSelector(selectPostingVirtualCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const activeRCNs = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const brandName = useSelector(selectBrandName)
  const hideRecurringOption = brandName === SupportedBrands.Jpm

  useEffect((): void => {
    if (!statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded) {
      dispatchStatistics([
        appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
      ])
    }
  }, [statisticsLoaded, dispatchStatistics])

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments))
  }
  const dispatchPostVirtualCardAndOpenToastAndCloseFlyout = (
    body: Partial<PostVirtualCardRequest>,
  ): void => {
    dispatch(postVirtualCardAndOpenToastAndCloseFlyout(body))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const [screen, setScreen] = useState(RequestVirtualCardScreen.Pick)

  useEffect((): void => {
    dispatch(createSetReceiptAttachmentIds([]))
  }, [screen])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    if (userOrganization === undefined) return
    dispatch(
      getOrgMembers(userOrganization.id, {
        search: filterInput,
        page: page,
        perPage: 10,
        showDeactivatedUsers: false,
        renderMetrics: false,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      <Helmet>
        <title>{`Request a Virtual Card | ${appName}`}</title>
        <meta
          name="description"
          content={`Request a Virtual Card page of ${appName}`}
        />
      </Helmet>
      {screen === RequestVirtualCardScreen.Pick && (
        <RequestVirtualCardPickContent
          setScreen={setScreen}
          userHasOrg={!!userOrganization}
          userHasActiveRCNs={!!activeRCNs}
          standardDisabled={!userOrganization && !!activeRCNs}
          recurringDisabled={!userOrganization && !!activeRCNs}
          hideRecurring={hideRecurringOption}
        />
      )}
      {screen === RequestVirtualCardScreen.Standard && (
        <RequestVirtualCardStandardContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
        />
      )}
      {screen === RequestVirtualCardScreen.Recurring && (
        <RequestVirtualCardRecurringContent
          receiptAttachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          postingVirtualCard={postingVirtualCard}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          postVirtualCardAndOpenToastAndCloseFlyout={
            dispatchPostVirtualCardAndOpenToastAndCloseFlyout
          }
          setScreen={setScreen}
          topLevelError={topLevelError}
          errorDetailMap={errorDetailMap}
          userOptions={filteredUserEmailOptions}
          recentUserOptions={filteredRecentUserEmailOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
        />
      )}
    </RightFlyout>
  )
}

export default RequestVirtualCard
